/**
 * Partner Bank Account Management
 * Self-service page for partners to manage their bank accounts
 * Used by both Service Partners and Channel Partners
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControlLabel, Checkbox,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Tooltip, MenuItem, Select, FormControl, InputLabel,
  InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, Star, StarBorder, AccountBalance, Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { updateScreenTitle } from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import PartnerBankAccountService from '../../../../services/partnerBankAccountService';

// UAE Bank List
const UAE_BANKS = [
  'Emirates NBD',
  'First Abu Dhabi Bank (FAB)',
  'Abu Dhabi Commercial Bank (ADCB)',
  'Dubai Islamic Bank',
  'Abu Dhabi Islamic Bank (ADIB)',
  'Mashreq Bank',
  'Commercial Bank of Dubai',
  'RAK Bank',
  'Sharjah Islamic Bank',
  'National Bank of Fujairah',
  'HSBC UAE',
  'Standard Chartered UAE',
  'Citibank UAE',
  'Other'
];

const PartnerBankAccountManagement = () => {
  const dispatch = useDispatch();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [originalAccountNumber, setOriginalAccountNumber] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    iban: '',
    swiftCode: '',
    branchName: '',
    isPrimary: false
  });

  // Get organization ID from auth
  const getOrganisationId = () => {
    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      return authInfo?.principalInfo?.organisationId ||
             authInfo?.user?.organisation?.id ||
             localStorage.getItem('organizationId');
    } catch (e) {
      console.error('Error getting organisation ID:', e);
      return null;
    }
  };

  const organisationId = getOrganisationId();

  useEffect(() => {
    dispatch(updateScreenTitle('Bank Accounts'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  useEffect(() => {
    if (organisationId) {
      loadBankAccounts();
    }
  }, [organisationId]);

  const loadBankAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const accounts = await PartnerBankAccountService.getByOrganisation(organisationId);
      setBankAccounts(accounts);
    } catch (err) {
      console.error('Failed to load bank accounts:', err);
      setError('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setOriginalAccountNumber(account.accountNumber || '');
      setFormData({
        accountHolderName: account.accountHolderName || '',
        bankName: account.bankName || '',
        accountNumber: account.accountNumber || '',
        confirmAccountNumber: '',
        iban: account.iban || '',
        swiftCode: account.swiftCode || '',
        branchName: account.branchName || '',
        isPrimary: account.isPrimary || false
      });
    } else {
      setEditingAccount(null);
      setOriginalAccountNumber('');
      setFormData({
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        confirmAccountNumber: '',
        iban: '',
        swiftCode: '',
        branchName: '',
        isPrimary: bankAccounts.length === 0 // First account is primary by default
      });
    }
    setShowAccountNumber(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
    setOriginalAccountNumber('');
    setShowAccountNumber(false);
    setFormData({
      accountHolderName: '',
      bankName: '',
      accountNumber: '',
      confirmAccountNumber: '',
      iban: '',
      swiftCode: '',
      branchName: '',
      isPrimary: false
    });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.accountHolderName || !formData.bankName || !formData.accountNumber) {
      setError('Please fill in all required fields');
      return;
    }

    // Account number confirmation validation
    const isNewAccount = !editingAccount;
    const accountNumberChanged = editingAccount && formData.accountNumber !== originalAccountNumber;
    
    if (isNewAccount || accountNumberChanged) {
      if (!formData.confirmAccountNumber) {
        setError('Please confirm your account number');
        return;
      }
      if (formData.accountNumber !== formData.confirmAccountNumber) {
        setError('Account numbers do not match');
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      if (editingAccount) {
        await PartnerBankAccountService.update(editingAccount.id, formData);
        setSuccess('Bank account updated successfully');
      } else {
        await PartnerBankAccountService.create(organisationId, formData);
        setSuccess('Bank account added successfully');
      }
      handleCloseDialog();
      loadBankAccounts();
    } catch (err) {
      console.error('Failed to save bank account:', err);
      setError(err.response?.data?.message || 'Failed to save bank account');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await PartnerBankAccountService.setPrimary(accountId);
      setSuccess('Default bank account updated');
      loadBankAccounts();
    } catch (err) {
      console.error('Failed to set primary:', err);
      setError('Failed to update default account');
    }
  };

  const handleDeactivate = async (accountId) => {
    if (!window.confirm('Are you sure you want to remove this bank account?')) {
      return;
    }
    try {
      await PartnerBankAccountService.deactivate(accountId);
      setSuccess('Bank account removed');
      loadBankAccounts();
    } catch (err) {
      console.error('Failed to deactivate:', err);
      setError(err.response?.data?.message || 'Failed to remove bank account');
    }
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (!organisationId) {
    return (
      <Box p={3}>
        <Alert severity="error">Organisation not found. Please log in again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalance color="primary" />
          <Typography variant="h5" fontWeight={600}>My Bank Accounts</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#064d51', '&:hover': { bgcolor: '#053a3d' } }}
        >
          Add Bank Account
        </Button>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Info Box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Payments from Thaklis will be sent to your default bank account.
        Please ensure your bank details are correct and up to date.
      </Alert>

      {/* Bank Accounts List */}
      <Card>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : bankAccounts.length === 0 ? (
          <Box p={4} textAlign="center">
            <AccountBalance sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Bank Accounts Added
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add your bank account to receive payments from Thaklis
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Bank Account
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a8a7e' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Bank Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Account Holder</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Account Number</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>IBAN</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankAccounts.filter(acc => acc.isActive !== false).map((account) => (
                  <TableRow key={account.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalance fontSize="small" color="action" />
                        <Typography fontWeight={500}>{account.bankName}</Typography>
                        {account.isPrimary && (
                          <Chip label="Default" size="small" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{account.accountHolderName}</TableCell>
                    <TableCell>{account.accountNumber}</TableCell>
                    <TableCell>{account.iban || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={account.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        color={account.isActive !== false ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!account.isPrimary && (
                        <Tooltip title="Set as Default">
                          <IconButton
                            size="small"
                            onClick={() => handleSetPrimary(account.id)}
                          >
                            <StarBorder fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {account.isPrimary && (
                        <Tooltip title="Default Account">
                          <IconButton size="small" disabled>
                            <Star fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(account)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!account.isPrimary && (
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeactivate(account.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Account Holder Name *"
              value={formData.accountHolderName}
              onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Bank Name *</InputLabel>
              <Select
                value={formData.bankName}
                label="Bank Name *"
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              >
                {UAE_BANKS.map((bank) => (
                  <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Account Number *"
              value={formData.accountNumber}
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  accountNumber: e.target.value,
                  // Clear confirmation when account number changes in edit mode
                  confirmAccountNumber: editingAccount && e.target.value !== originalAccountNumber ? '' : formData.confirmAccountNumber
                });
              }}
              fullWidth
              type={showAccountNumber ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAccountNumber(!showAccountNumber)}
                      edge="end"
                      size="small"
                    >
                      {showAccountNumber ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Show confirm field for new accounts OR when account number is changed in edit mode */}
            {(!editingAccount || (editingAccount && formData.accountNumber !== originalAccountNumber)) && (
              <TextField
                label="Confirm Account Number *"
                value={formData.confirmAccountNumber}
                onChange={(e) => setFormData({ ...formData, confirmAccountNumber: e.target.value })}
                fullWidth
                type="password"
                placeholder="Re-enter account number"
                error={formData.confirmAccountNumber && formData.accountNumber !== formData.confirmAccountNumber}
                helperText={formData.confirmAccountNumber && formData.accountNumber !== formData.confirmAccountNumber ? 'Account numbers do not match' : ''}
              />
            )}

            <TextField
              label="IBAN"
              value={formData.iban}
              onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
              fullWidth
              placeholder="AE..."
              helperText="International Bank Account Number"
            />

            <TextField
              label="SWIFT/BIC Code"
              value={formData.swiftCode}
              onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value.toUpperCase() })}
              fullWidth
            />

            <TextField
              label="Branch Name"
              value={formData.branchName}
              onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isPrimary}
                  onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                />
              }
              label="Set as default account for payments"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || ((!editingAccount || formData.accountNumber !== originalAccountNumber) && (formData.accountNumber !== formData.confirmAccountNumber || !formData.confirmAccountNumber))}
            sx={{ bgcolor: '#064d51', '&:hover': { bgcolor: '#053a3d' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editingAccount ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerBankAccountManagement;
